import { T, useGT } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import {
  ArrowSquareOutIcon,
  BracketsCurlyIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import {
  useGetWorkflowDefinition,
  useGetWorkflow,
} from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { definitionToGraph } from 'src/lib/workflows/definitionToGraph';
import { WorkflowGraphView } from 'src/components/workflows/WorkflowGraphView';

interface WorkflowActionPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowDefinitionId: string;
  stepLabel: string;
}

export function WorkflowActionPreviewDialog({
  open,
  onOpenChange,
  workflowDefinitionId,
  stepLabel,
}: WorkflowActionPreviewDialogProps) {
  const gt = useGT();
  const { data: definition, isLoading: isDefinitionLoading } =
    useGetWorkflowDefinition(open ? workflowDefinitionId : undefined);
  const { data: workflow, isLoading: isWorkflowLoading } = useGetWorkflow(
    open ? definition?.workflowId : undefined,
  );

  const isLoading = isDefinitionLoading || isWorkflowLoading;

  const graph = useMemo(() => {
    if (!definition) return null;
    return definitionToGraph(definition);
  }, [definition]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <BracketsCurlyIcon className="h-5 w-5 text-muted-foreground" />
            <DialogTitle>{stepLabel}</DialogTitle>
            {workflow && (
              <span className="text-sm text-muted-foreground">
                — {workflow.name}
              </span>
            )}
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <CircleNotchIcon className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !definition || !workflow || !graph ? (
          <p className="py-4 text-sm text-muted-foreground">
            <T context="error when workflow action definition not found">
              Could not load the workflow action definition.
            </T>
          </p>
        ) : (
          <div className="space-y-3">
            <div
              className="rounded-lg border"
              style={{ height: '400px', width: '100%' }}
            >
              <WorkflowGraphView
                nodes={graph.nodes}
                edges={graph.edges}
                zipTies={definition.zipTies}
                showControls
                showBackground
                interactive
                nested
              />
            </div>

            <div className="flex justify-end">
              <Button variant="outline" size="sm" asChild>
                <Link
                  to="/workflows/$workflowId/v/$versionNumber"
                  params={{
                    workflowId: workflow.id,
                    versionNumber: String(definition.version),
                  }}
                  target="_blank"
                >
                  <ArrowSquareOutIcon className="mr-1.5 h-4 w-4" />
                  {gt('Open workflow', {
                    context:
                      'button to open the linked workflow action definition',
                  })}
                </Link>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
